import * as _ from 'lodash';
import moment from 'moment';
import { Organization } from "./organization";
import { Conference, ConferenceType, Sport, Teamlevel } from './conference';
import { User, UserRoleValue } from "./user";
import { Country, State } from './locations';
import { Game } from './game';
import { ThirdParty } from './third_parties';

export class Team {
  _id: string;
  name: string;
  mascot?: string;
  active: boolean;
  teamlevelid?: Teamlevel['_id'];
  organizationid?: Organization['_id'];
  sportid?: Sport; // for single accounts
  subscriptionExpires?: Date;
  profitshare?: number;
  seasonprice?: number;
  gameprice?: number;
  offenseType?: OffenseType;
  defenseType?: DefenseType;
	payForAccess?: boolean;
	accessPrice?: number;
	lastLeaguePoolUpload?: Date;
	lastLeaguePoolUnlock?: Date;
	lastLeaguePoolUploadGameid?: Game['_id'] | 'Bye' | 'Override';
	leaguePoolByeWeekApprovedDate?: Date;
	autoUnlockLeaguePool?: boolean;
	thirdpartyid?: ThirdParty['_id'];
  messengerDisabled?: boolean;
  highlightsDisabled?: boolean;
  betaTestingEnabled?: boolean;
  youtubeAccessToken?: string;
  youtubeRefreshToken?: string;
  youtubeTokenExpiry?: Date;
  oldid?: number;
	createdAt?: Date;
	updatedAt?: Date;


  //not in db
  organization?: Organization;
  teamlevel?: Teamlevel;
  conference?: Conference;
  country?: Country;
  state?: State;
  city?: string;
	thirdParty?: ThirdParty;
  thumbnail?: string;
  loading?: boolean;
}

export interface TeamSettings {
  _id: string;
  teamid: Team['_id'];
  gradingStrategyId?: string;
  customGrades: CustomGrade[];
  hideDepthChart: boolean;
  hub?: TeamHub;
  recruitingContact: string; //userid
  hideStatsFromAthletes?: boolean;
  hideStatsPagesFromAthletesAndParents?: boolean;
	autoRecordPractices?: boolean;
	defaultCameraidGame?: string;
	defaultCameraidPractice?: string;
	scoreboardOverlayByCamera?: Record<string, boolean>;
	scoreboardPositionByCamera?: Record<string, ScoreboardOverlayPosition>;
	defaultRecordTime?: number | null;
	sportDefaultRecordTime?: number;
	effectiveDefaultRecordTime?: number;
	autoHideGames?: boolean;
  parentAbilities?: {
    watchFilm?: boolean;
    viewStats?: boolean;
    uploadFilm?: boolean;
    viewPlaybook?: boolean;
    editMediaLibrary?: boolean; 
  }
  parentPrice?: number;
	defaultSeasonid?: number;
}

export type ScoreboardOverlayPosition = 'bottom-left' | 'bottom-right' | 'top-right' | 'top-left';

export interface AutoHighlightSettings {
  _id?: string;
  teamid?: Team['_id'];
  buildTeamHighlights?: boolean;
  buildPlayerHighlights?: boolean;
  teamHighlightsPublic?: boolean;
  playerHighlightsPublic?: boolean;
  musicIds?: string[];
  teamMusicIds?: string[];
  playerMusicIds?: string[];
  paAnnouncerEnabled?: boolean;
  paAnnouncerPlayerNamesEnabled?: boolean;
  teamPaAnnouncerEnabled?: boolean;
  teamPaAnnouncerPlayerNamesEnabled?: boolean;
  playerPaAnnouncerEnabled?: boolean;
  playerPaAnnouncerPlayerNamesEnabled?: boolean;
  teamRules?: AutoHighlightRuleSettings[];
  playerRules?: AutoHighlightRuleSettings[];
}

export interface UserAutoHighlightSettings {
  _id?: string;
  userid?: User['_id'];
  teamid?: Team['_id'];
  buildPlayerHighlights?: boolean;
  playerHighlightsPublic?: boolean;
  musicIds?: string[];
  paAnnouncerEnabled?: boolean;
  paAnnouncerPlayerNamesEnabled?: boolean;
  playerRules?: AutoHighlightRuleSettings[];
  hasUserSettings?: boolean;
}

export interface AutoHighlightRuleSettings {
  key: string;
  enabled?: boolean;
  order: number;
  parameters?: AutoHighlightRuleParameters;
}

export interface AutoHighlightRuleParameters {
  minYards?: number;
}

export interface CustomGrade {
  _id: string;
  name: string;
  gradingStrategyId: string;
}

export interface GameStorageData  {
  gameid: number;
  seasonid: number;
  scout: boolean;
  practice: boolean;
  archived: boolean;
  size: number; //size in bytes
  hours: number; //size in hours
}

export interface AwsCostRates {
  storageGbMonth: number;
  archivedStorageGbMonth: number;
  mediaConvertMinute: number;
  dataTransferGb: number;
}

export interface AwsUsageReportOptions {
  teamid?: string;
  startDate?: Date;
  endDate?: Date;
  months?: number;
  sportid?: Sport;
  conferenceid?: string;
  conferenceType?: ConferenceType;
  teamlevelid?: string;
  countryid?: string;
  stateid?: string;
  organizationid?: string;
  activeOnly?: boolean;
  maxTeams?: number;
  rates?: Partial<AwsCostRates>;
}

export interface AwsTeamUsageReport {
  teamid: string;
  teamName: string;
  sportid: Sport | string;
  conferenceid?: string;
  conferenceName?: string;
  conferenceType?: ConferenceType;
  teamlevelid?: string;
  teamlevelName?: string;
  totalStorageHours: number;
  archivedStorageHours: number;
  uploadHours: number;
  viewHours: number;
  storageCost: number;
  uploadCost: number;
  dataTransferCost: number;
  totalCost: number;
}

export interface AwsUsageReportSummary {
  key: string;
  label: string;
  teamCount: number;
  totalStorageHours: number;
  archivedStorageHours: number;
  uploadHours: number;
  viewHours: number;
  storageCost: number;
  uploadCost: number;
  dataTransferCost: number;
  totalCost: number;
  averageTeamCost: number;
  maxTeam?: {
    teamid: string;
    teamName: string;
    teamlevelName?: string;
    totalCost: number;
  };
}

export interface AwsUsageReport {
  rangeStart: Date;
  rangeEnd: Date;
  months: number;
  rates: AwsCostRates;
  transferCostCalculating: boolean;
  filters: any;
  overall: AwsUsageReportSummary;
  grouped: {[group: string]: AwsUsageReportSummary[]};
  perTeam: AwsTeamUsageReport[];
}

export interface StorageAddition {
  _id?: string;
  teamid: Team['_id'];
  hours: number;
  description: string;
  dateAdded: Date;
  amountPaid?: number;
}

export interface FormationMapping {
  _id?: string;
  teamid: Team['_id'];
  backfields?: {[key: string]: string | null};
  formations?: {[key: string]: string | null};
  perspectives?: FormationPerspective[];
}

export interface FormationPerspective {
  perspectiveId: string;
  name: string;
  columnName: string;
  formations?: {[key: string]: string | null};
}

export interface FormationPerspectiveMutationResult {
  mapping: FormationMapping;
  statColumns?: {columns?: any[]};
}

export type FormationPerspectiveValueSource = 'stat' | 'perspective' | 'default' | 'qwikcut';

export function getFormationPerspectiveValue(stat: any, columnName: string, mapping: FormationMapping): {value: string; source: FormationPerspectiveValueSource} {
  const explicitValue = stat?.[columnName];
  const perspective = _.find(mapping?.perspectives || [], {columnName});
  const formationName = stat?.formation;
  if (!perspective || !formationName) return {value: explicitValue, source: 'stat'};
  const mappingKeys = _.uniq([..._.keys(mapping.formations || {}), ..._.keys(perspective.formations || {})]);
  let canonicalName = _.includes(mappingKeys, formationName) ? formationName : null;
  if (!canonicalName) {
    const matches = _.filter(mappingKeys, key => mapping.formations?.[key] === formationName);
    if (matches.length === 1) canonicalName = matches[0];
  }
  const fallback = canonicalName && _.trim(perspective.formations?.[canonicalName] || '')
    ? {value: perspective.formations[canonicalName], source: 'perspective' as FormationPerspectiveValueSource}
    : canonicalName && _.trim(mapping.formations?.[canonicalName] || '')
      ? {value: mapping.formations[canonicalName], source: 'default' as FormationPerspectiveValueSource}
      : {value: canonicalName || formationName, source: 'qwikcut' as FormationPerspectiveValueSource};
  if (explicitValue != null && _.trim(_.toString(explicitValue)) && explicitValue !== fallback.value) return {value: explicitValue, source: 'stat'};
  return fallback;
}

export interface TeamHub {
  bannerImage?: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  about?: string;
  hidden?: boolean;
  customHeaderText?: string;
  headerTextColor?: string;
  hideHeaderText?: boolean;
}

export interface TeamGroup {
  _id?: string;
  teamid: Team['_id'];
  name: string;
  groupmembers?: User['_id'][];
  oldid?: number;
}

export interface Login {
  _id?: string;
  userid: User['_id'];
  ipaddress?: string;
  city?: string;
  region?: string;
  date?: Date;
  browsername?: string;
  browsertype?: string;
  devicetype?: string;
  devicesubtype?: string;
  osname?: string;
  osalias?: string;
}

export interface TeamRequest {
  _id: string;
  email: string;
  firstname: string;
  lastname: string;
	phonenumber?: string;
	jerseyNumber?: number;
  userrole?: UserRoleValue;
  teamid: Team['_id'];
  requestdate: Date;
  approved?: boolean;
  denied?: boolean;
}

export interface TeamTodo {
  _id?: string;
  teamid: Team['_id'];
  items: TodoItem[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface TodoItem {
  type: TodoType;
  completed?: boolean;
  completedDate?: Date;

  //not in db
  loading?: boolean;
}

export interface TeamNotificationSettings {
  _id?: string;
  teamid: Team['_id'];
  gameUploads: TeamNotificationObject;
  playlistShared: TeamNotificationObject;
  playbookShared: TeamNotificationObject;
  breakdowns: TeamNotificationObject;
  cameraUpdates: TeamNotificationObject;
  highlights: TeamNotificationObject;
}

export class TeamNotificationObject {
  parent: NotificationOption = new NotificationOption;
  athlete: NotificationOption = new NotificationOption;
  coach: NotificationOption = new NotificationOption;
  teamadmin: NotificationOption = new NotificationOption;
  boardmember: NotificationOption = new NotificationOption;
}

export class NotificationOption {
  email: boolean = true;
  push: boolean = true;
}

export type TodoType = 'coaches' | 'roster' | 'groups' | 'recruiting' | 'schedule' | 'hub' | 'prospects';

export type OffenseType = 'Spread' | 'Air Raid' | 'Pro' | 'Wing T' | 'Split Veer' | 'Flex Bone Triple Option' | 'Single Wing';
export type DefenseType = '3-3 Stack' | '3-4' | '4-2-2' | '4-3' | '5-2' | '5-3' | '4-4' | '3-5-2';

export const offenseTypes: OffenseType[] = ['Spread', 'Air Raid', 'Pro', 'Wing T', 'Split Veer', 'Flex Bone Triple Option', 'Single Wing'];
export const defenseTypes: DefenseType[] = ['3-3 Stack', '3-4', '4-2-2', '4-3', '5-2', '5-3', '4-4', '3-5-2'];

export const seasons = _.range(moment().add(8, 'months').toDate().getFullYear(), 2004);

export interface SeasonDisplaySummary {
	all: number[];
	visible: number[];
	overflowCount: number;
	tooltip: string;
}

export const formatCompactSeason = (season: number): string => {
	if (!Number.isFinite(season)) return '';
	const startYear = _.padStart(_.toString(season % 100), 2, '0');
	const endYear = _.padStart(_.toString((season + 1) % 100), 2, '0');
	return `${startYear}-${endYear}`;
};

export const formatFullSeason = (season: number): string => {
	if (!Number.isFinite(season)) return '';
	return `${season}-${season + 1}`;
};

export const getSeasonDisplaySummary = (selectedSeasons: number[] = [], visibleLimit: number = 3): SeasonDisplaySummary => {
	const all = _.orderBy(_.filter(selectedSeasons, season => Number.isFinite(season)), season => season, 'desc');
	const visible = _.take(all, visibleLimit);
	return {
		all,
		visible,
		overflowCount: Math.max(all.length - visible.length, 0),
		tooltip: _.map(all, formatFullSeason).join(', ')
	};
};

export type ParentAbility = 'watchFilm' | 'viewStats' | 'uploadFilm' | 'viewPlaybook' | 'editMediaLibrary';
export const parentAbilityTypes: ParentAbility[] = ['watchFilm', 'viewStats', 'uploadFilm', 'viewPlaybook', 'editMediaLibrary'];
export const defaultParentAbilities: Required<NonNullable<TeamSettings['parentAbilities']>> = {
  watchFilm: true,
  viewStats: false,
  uploadFilm: false,
  viewPlaybook: false,
  editMediaLibrary: false
};

export const normalizeParentAbilities = (
  abilities?: TeamSettings['parentAbilities']
): Required<NonNullable<TeamSettings['parentAbilities']>> => ({
  ...defaultParentAbilities,
  ...(abilities || {})
});

export const parentAbilityIsAllowed = (
  settings: TeamSettings | null | undefined,
  ability: ParentAbility
): boolean => !!settings && normalizeParentAbilities(settings.parentAbilities)[ability];

export const statsAreHiddenForUser = (
  settings: TeamSettings | null | undefined,
  role: UserRoleValue | null | undefined
): boolean => _.includes(<UserRoleValue[]>['athlete', 'parent'], role) && settings?.hideStatsPagesFromAthletesAndParents === true;

export const statsAreAllowedForUser = (
  settings: TeamSettings | null | undefined,
  role: UserRoleValue | null | undefined
): boolean => !_.includes(<UserRoleValue[]>['athlete', 'parent'], role) ||
  (!!settings && !statsAreHiddenForUser(settings, role) && (role !== 'parent' || parentAbilityIsAllowed(settings, 'viewStats')));

export interface TeamMetrics {
	teamid: string;
	teamname: string;
	numUsers: number;
	numCoaches: number;
	numAthletes: number;
	recentLogins: {name: string, email: string, lastLogin: Date, roleDisplay: string}[];
	numGames: number;
	numScoutGames: number;
	numPractices: number;
	totalBreakdownsSubmitted: number;
	teamBreakdownsSubmitted: number;
	singleBreakdownsSubmitted: number;
	highlightsCreated: number;
	highlightsUnpublished: number;
	highlightsPublished: number;
	playbookPlays: number;
	numProspects: number;
	createdAt: Date;
	subscriptionExpires: Date;
}
